import {
  useMemo,
  useRef,
  useState,
} from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  DatabaseBackup,
  Download,
  FileDown,
  FileUp,
  ShieldCheck,
} from "lucide-react";
import {
  Card,
  InlineNotice,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { clearAllLocalBusinessData, db } from "../lib/db";
import { flushSyncQueue } from "../lib/sync";
import {
  downloadText,
  parseCsv,
  toCsv,
} from "../lib/csv";
import { refreshFromServer } from "../lib/data";
import type { CachedProduct } from "../lib/db";
import {
  parseSpreadsheetFile,
  downloadTemplateExcel,
  type TemplateType,
} from "../lib/spreadsheet";
import {
  ColumnMappingModal,
  type FieldDefinition,
} from "../components/ColumnMappingModal";

const PRODUCT_HEADERS = [
  "sku",
  "name",
  "businessVertical",
  "category",
  "subcategory",
  "barcode",
  "baseUnit",
  "salePrice",
  "minStockQty",
  "trackExpiry",
  "unitsJson",
];

interface ImportPreview {
  rows: Array<Record<string, string>>;
  valid: number;
  errors: string[];
}

interface BackupValidation {
  valid: boolean;
  errors: string[];
  counts: Record<string, number>;
  source?: {
    storeName?: string;
    portable?: boolean;
  } | null;
  generatedAt?: string | null;
}

interface BackupPreview {
  name: string;
  backup: Record<string, unknown>;
  validation: BackupValidation;
}

function parseNumber(
  value: string,
  fallback = 0,
): number {
  if (!value.trim()) return fallback;
  const parsed = Number(value.trim());
  if (!Number.isFinite(parsed)) {
    throw new Error(`Không phải số: ${value}`);
  }
  return parsed;
}

function parseBoolean(value: string): boolean {
  const normalized = value
    .trim()
    .toLowerCase();
  return ["1", "true", "yes", "có", "co"].includes(
    normalized,
  );
}

function productPayload(
  row: Record<string, string>,
) {
  const unitsRaw = row.unitsJson?.trim();
  let units: unknown[] = [];

  if (unitsRaw) {
    const parsed = JSON.parse(unitsRaw);
    if (!Array.isArray(parsed)) {
      throw new Error(
        "unitsJson phải là một mảng JSON.",
      );
    }
    units = parsed;
  }

  const trackExpiry = parseBoolean(
    row.trackExpiry ?? "",
  );

  if (!row.sku?.trim() || !row.name?.trim()) {
    throw new Error("Thiếu sku hoặc name.");
  }

  return {
    sku: row.sku.trim(),
    name: row.name.trim(),
    barcode: row.barcode?.trim() || null,
    businessVertical: row.businessVertical?.trim() || null,
    category: row.category?.trim() || null,
    subcategory: row.subcategory?.trim() || null,
    baseUnit:
      row.baseUnit?.trim() || "cái",
    salePrice: Math.round(
      parseNumber(row.salePrice ?? "", 0),
    ),
    openingCost: 0,
    openingQty: 0,
    minStockQty: parseNumber(
      row.minStockQty ?? "",
      0,
    ),
    trackExpiry,
    openingLotCode: null,
    openingExpiryDate: null,
    units,
  };
}

export function DataToolsPage() {
  const auth = useAuth();
  const fileRef =
    useRef<HTMLInputElement | null>(null);
  const backupFileRef =
    useRef<HTMLInputElement | null>(null);
  const products = useLiveQuery(
    () => db.products.orderBy("name").toArray(),
    [],
    [],
  );
  const money = useLiveQuery(
    () =>
      db.money
        .orderBy("createdAt")
        .toArray(),
    [],
    [],
  );
  const [preview, setPreview] =
    useState<ImportPreview | null>(null);
  const [backupPreview, setBackupPreview] =
    useState<BackupPreview | null>(null);
  const pendingSync = useLiveQuery(
    () =>
      db.syncQueue
        .where("status")
        .anyOf("PENDING", "FAILED", "SYNCING")
        .count(),
    [],
    0,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] =
    useState("");
  const [error, setError] = useState("");

  const canSnapshot =
    !auth.demo &&
    navigator.onLine &&
    ["OWNER", "ACCOUNTANT"].includes(
      auth.session?.user.role ?? "",
    );
  const canRestore =
    !auth.demo &&
    navigator.onLine &&
    (auth.session?.user.role ?? "") === "OWNER" &&
    pendingSync === 0;

  const productCsv = useMemo(
    () =>
      toCsv(
        PRODUCT_HEADERS,
        products.map((product) => ({
          sku: product.sku,
          name: product.name,
          barcode: product.barcode ?? "",
          businessVertical: product.businessVertical ?? "",
          category: product.category ?? "",
          subcategory: product.subcategory ?? "",
          baseUnit: product.baseUnit,
          salePrice: product.salePrice,
          minStockQty:
            product.minStockQty,
          trackExpiry:
            product.trackExpiry
              ? "true"
              : "false",
          unitsJson: JSON.stringify(
            product.units.map((unit) => ({
              name: unit.name,
              multiplier:
                unit.multiplier,
              barcode:
                unit.barcode ?? null,
              salePrice:
                unit.salePrice ?? null,
            })),
          ),
        })),
      ),
    [products],
  );

  function exportProducts() {
    downloadText(
      `bledger-products-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      productCsv,
      "text/csv;charset=utf-8",
    );
  }

  function exportMoney() {
    const csv = toCsv(
      [
        "createdAt",
        "direction",
        "amount",
        "category",
        "accountType",
        "syncStatus",
      ],
      money.map((item) => ({
        createdAt: item.createdAt,
        direction: item.direction,
        amount: item.amount,
        category: item.category,
        accountType: item.accountType,
        syncStatus: item.syncStatus,
      })),
    );
    downloadText(
      `bledger-cash-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      csv,
      "text/csv;charset=utf-8",
    );
  }

  function downloadTemplate() {
    const csv = toCsv(
      PRODUCT_HEADERS,
      [
        {
          sku: "SP001",
          name: "Sản phẩm mẫu",
          businessVertical: "Tạp hóa & Bách hóa",
          category: "Đồ uống",
          subcategory: "Nước giải khát",
          barcode: "",
          baseUnit: "chai",
          salePrice: 12000,
          minStockQty: 6,
          trackExpiry: "false",
          unitsJson:
            '[{"name":"thùng","multiplier":24,"barcode":null,"salePrice":250000}]',
        },
      ],
    );

    downloadText(
      "bledger-product-import-template.csv",
      csv,
      "text/csv;charset=utf-8",
    );
  }

  async function exportSnapshot() {
    if (!canSnapshot) return;
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const snapshot =
        await api<Record<string, unknown>>(
          "/exports/snapshot",
        );
      downloadText(
        `bledger-snapshot-${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.json`,
        JSON.stringify(
          snapshot,
          null,
          2,
        ),
        "application/json;charset=utf-8",
      );
      setMessage(
        "Đã tạo snapshot dữ liệu nghiệp vụ. File không chứa mật khẩu, cookie hoặc secret.",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể tạo snapshot",
      );
    } finally {
      setBusy(false);
    }
  }

  async function exportBackup() {
    if (!canSnapshot) return;
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const backup = await api<Record<string, unknown>>("/backups/export");
      const storeName = String(auth.session?.store.name ?? "store")
        .replace(/[^a-zA-Z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      downloadText(
        `bledger-backup-${storeName || "store"}-${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.json`,
        JSON.stringify(backup, null, 2),
        "application/json;charset=utf-8",
      );
      setMessage(
        "Đã tạo bản sao lưu đầy đủ từ dữ liệu server. File có checksum và không chứa mật khẩu, session hoặc secret.",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể tạo bản sao lưu.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function chooseBackupFile(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    setError("");
    setMessage("");
    setBackupPreview(null);

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
      const validation = await api<BackupValidation>("/backups/validate", {
        method: "POST",
        body: JSON.stringify({ backup: parsed }),
      });
      setBackupPreview({
        name: file.name,
        backup: parsed,
        validation,
      });
      if (!validation.valid) {
        setError(validation.errors.join(" "));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không đọc hoặc xác thực được file backup.",
      );
    } finally {
      event.target.value = "";
    }
  }

  async function restoreBackup() {
    if (!backupPreview?.validation.valid || !canRestore) return;

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const syncResult = await flushSyncQueue();
      if (syncResult.unauthorized) {
        auth.invalidateSession();
        return;
      }

      const unresolved = await db.syncQueue
        .where("status")
        .anyOf("PENDING", "FAILED", "SYNCING")
        .count();
      if (unresolved > 0) {
        throw new Error(
          "Còn dữ liệu trên thiết bị chưa đồng bộ. Hãy xử lý đồng bộ trước khi khôi phục.",
        );
      }

      // Safety net: always download the current server state before destructive restore.
      const safetyBackup = await api<Record<string, unknown>>("/backups/export");
      downloadText(
        `bledger-before-restore-${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.json`,
        JSON.stringify(safetyBackup, null, 2),
        "application/json;charset=utf-8",
      );

      const storeName = auth.session?.store.name ?? "";
      const confirmation = window.prompt(
        `Khôi phục sẽ thay thế dữ liệu nghiệp vụ hiện tại.\n\nMột backup an toàn vừa được tải xuống.\n\nNhập chính xác tên cửa hàng để xác nhận:\n${storeName}`,
      );
      if (confirmation == null) return;

      await api("/backups/restore", {
        method: "POST",
        body: JSON.stringify({
          backup: backupPreview.backup,
          confirmation,
        }),
      });

      await clearAllLocalBusinessData();
      await refreshFromServer();
      setBackupPreview(null);
      setMessage(
        "Khôi phục hoàn tất. Cache thiết bị đã được làm mới từ dữ liệu server.",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể khôi phục dữ liệu.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function chooseFile(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    setError("");
    setMessage("");
    setPreview(null);

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsed = await parseSpreadsheetFile(file);
      const rows = parsed.rows;

      const errors: string[] = [];
      let valid = 0;
      const seenSku = new Set<string>();

      rows.forEach((row, index) => {
        try {
          const payload =
            productPayload(row);
          const key = payload.sku
            .toLocaleLowerCase("vi");

          if (seenSku.has(key)) {
            throw new Error(
              "SKU bị trùng trong file.",
            );
          }
          seenSku.add(key);

          if (
            products.some(
              (product) =>
                product.sku.toLocaleLowerCase(
                  "vi",
                ) === key,
            )
          ) {
            throw new Error(
              "SKU đã tồn tại trong cửa hàng.",
            );
          }

          valid += 1;
        } catch (err) {
          errors.push(
            `Dòng ${index + 2}: ${err instanceof Error ? err.message : "Dữ liệu không hợp lệ"}`,
          );
        }
      });

      setPreview({
        rows,
        valid,
        errors,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không đọc được tệp Excel/CSV",
      );
    } finally {
      event.target.value = "";
    }
  }

  async function importProducts() {
    if (!preview || preview.errors.length > 0) {
      return;
    }

    if (!auth.demo && !navigator.onLine) {
      setError(
        "Import Product Master cần kết nối mạng để kiểm tra trùng SKU/barcode.",
      );
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const payloads = preview.rows.map((row) => productPayload(row));

      if (auth.demo) {
        const localProducts: CachedProduct[] = payloads.map((payload) => ({
          id: crypto.randomUUID(),
          name: payload.name,
          sku: payload.sku,
          barcode: payload.barcode,
          businessVertical: payload.businessVertical,
          category: payload.category,
          subcategory: payload.subcategory,
          baseUnit: payload.baseUnit,
          salePrice: payload.salePrice,
          avgCost: 0,
          stockQty: 0,
          minStockQty: payload.minStockQty,
          trackExpiry: payload.trackExpiry,
          nextExpiryDate: null,
          units: (payload.units as Array<any>).map((unit) => ({
            id: crypto.randomUUID(),
            name: String(unit.name),
            multiplier: Number(unit.multiplier),
            barcode: unit.barcode ?? null,
            salePrice:
              unit.salePrice == null ? null : Number(unit.salePrice),
          })),
        }));

        await db.transaction("rw", db.products, async () => {
          await db.products.bulkAdd(localProducts);
        });
      } else {
        await api("/imports/batch-products", {
          method: "POST",
          body: JSON.stringify({ products: payloads }),
        });
        await refreshFromServer();
      }

      setPreview(null);
      setMessage(
        `Đã tạo ${payloads.length} SKU trong Product Master. Tồn kho và giá vốn vẫn bằng 0 cho đến khi thực hiện Nhập hàng hoặc Điều chỉnh tồn.`,
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể import Product Master.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Sao lưu & dữ liệu"
        description="Xuất dữ liệu để lưu trữ; nhập CSV chỉ dành cho danh mục sản phẩm"
      />

      {message ? (
        <InlineNotice
          tone="success"
          title={message}
        />
      ) : null}
      {error ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      <section>
        <div className="section-title">
          <div>
            <Download size={20} />
            <h2>Xuất dữ liệu</h2>
          </div>
        </div>

        <div className="data-tool-grid">
          <Card className="data-tool-card">
            <FileDown size={24} />
            <strong>Danh mục sản phẩm</strong>
            <p>
              CSV Product Master gồm SKU, mã vạch, đơn vị, giá bán,
              ngưỡng tồn, quản lý HSD và quy cách quy đổi.
            </p>
            <button
              className="btn btn-secondary"
              onClick={exportProducts}
            >
              Xuất CSV sản phẩm
            </button>
          </Card>

          <Card className="data-tool-card">
            <FileDown size={24} />
            <strong>Sổ tiền cục bộ</strong>
            <p>
              CSV các giao dịch tiền đang có
              trên thiết bị.
            </p>
            <button
              className="btn btn-secondary"
              onClick={exportMoney}
            >
              Xuất CSV sổ tiền
            </button>
          </Card>

          <Card className="data-tool-card">
            <DatabaseBackup size={24} />
            <strong>Sao lưu đầy đủ</strong>
            <p>
              JSON từ PostgreSQL/API gồm kho, lô, nhập/bán/hoàn hàng,
              sổ tiền, công nợ, đóng sổ, thuế và lịch sử hoạt động.
            </p>
            <button
              className="btn btn-primary"
              disabled={busy || !canSnapshot}
              onClick={() => void exportBackup()}
            >
              Sao lưu dữ liệu
            </button>
            {!canSnapshot ? (
              <small>Cần online và vai trò Chủ cửa hàng/Kế toán.</small>
            ) : null}
          </Card>

          <Card className="data-tool-card">
            <DatabaseBackup size={24} />
            <strong>
              Snapshot nghiệp vụ
            </strong>
            <p>
              JSON từ server gồm hàng hóa, lô,
              bán hàng, hoàn hàng, nhập hàng,
              tiền, công nợ và đóng sổ.
            </p>
            <button
              className="btn btn-primary"
              disabled={
                busy || !canSnapshot
              }
              onClick={() =>
                void exportSnapshot()
              }
            >
              Tạo snapshot JSON
            </button>
            {!canSnapshot ? (
              <small>
                Cần online và vai trò Chủ cửa
                hàng/Kế toán.
              </small>
            ) : null}
          </Card>
        </div>
      </section>

      <section>
        <div className="section-title">
          <div>
            <DatabaseBackup size={20} />
            <h2>Khôi phục dữ liệu</h2>
          </div>
          <StatusBadge tone="warning">Chỉ Chủ cửa hàng</StatusBadge>
        </div>

        <Card className="data-import-card">
          <InlineNotice
            tone="warning"
            title="Khôi phục có kiểm soát"
          >
            Hệ thống kiểm tra định dạng, checksum và phạm vi cửa hàng trước
            khi ghi. Trước khi thay thế dữ liệu, BLedger tự tải một backup
            trạng thái hiện tại. Không thể khôi phục khi còn lệnh offline
            chưa đồng bộ.
          </InlineNotice>

          <div className="data-import-actions">
            <a
              className="btn btn-secondary"
              href="/samples/bledger-backup-sample-3-months.json"
              download="bledger-backup-sample-3-months.json"
            >
              <Download size={16} />
              Tải backup mẫu 3 tháng
            </a>
            <button
              className="btn btn-secondary"
              disabled={busy || auth.demo || !navigator.onLine}
              onClick={() => backupFileRef.current?.click()}
            >
              <FileUp size={16} />
              Chọn file backup JSON
            </button>
            <input
              ref={backupFileRef}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(event) => void chooseBackupFile(event)}
            />
            <small>
              Đồng bộ chờ xử lý: {pendingSync ?? 0}
            </small>
          </div>

          {backupPreview ? (
            <div className="backup-preview">
              <div>
                <strong>{backupPreview.name}</strong>
                <small>
                  {backupPreview.validation.valid
                    ? "Backup hợp lệ"
                    : "Backup không hợp lệ"}
                </small>
              </div>
              <StatusBadge
                tone={backupPreview.validation.valid ? "success" : "danger"}
              >
                {backupPreview.validation.valid ? "Sẵn sàng" : "Bị chặn"}
              </StatusBadge>
              {backupPreview.validation.valid ? (
                <small className="backup-preview-counts">
                  {Object.entries(backupPreview.validation.counts)
                    .filter(([, count]) => count > 0)
                    .map(([key, count]) => `${key}: ${count}`)
                    .join(" · ")}
                </small>
              ) : null}
            </div>
          ) : null}

          {backupPreview?.validation.valid ? (
            <button
              className="btn btn-primary btn-lg"
              disabled={busy || !canRestore}
              onClick={() => void restoreBackup()}
            >
              <ShieldCheck size={20} />
              Khôi phục backup
            </button>
          ) : null}

          {!canRestore ? (
            <small>
              Khôi phục yêu cầu online, vai trò Chủ cửa hàng và không còn
              dữ liệu chờ đồng bộ.
            </small>
          ) : null}
        </Card>
      </section>

      <section>
        <div className="section-title">
          <div>
            <FileUp size={20} />
            <h2>Import Product Master / SKU</h2>
          </div>
          <StatusBadge tone="info">
            Không nhập giao dịch tài chính
          </StatusBadge>
        </div>

        <Card className="data-import-card">
          <InlineNotice
            tone="info"
            title="An toàn dữ liệu"
          >
            Chỉ tạo SKU/master data mới. Import này không làm phát sinh
            tồn kho, giá vốn, sale, tiền hoặc công nợ. Muốn có hàng trong
            kho, hãy thực hiện nghiệp vụ Nhập hàng sau đó.
          </InlineNotice>

          <div className="data-import-actions" style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <button
              className="btn btn-secondary"
              onClick={() => downloadTemplateExcel("PRODUCTS")}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <Download size={16} />
              Tải mẫu Excel (.xlsx)
            </button>
            <button
              className="btn btn-secondary"
              onClick={downloadTemplate}
            >
              Tải mẫu CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={() =>
                fileRef.current?.click()
              }
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FileUp size={16} />
              Chọn tệp Excel / CSV
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              hidden
              onChange={(event) =>
                void chooseFile(event)
              }
            />
          </div>

          {preview ? (
            <div className="import-preview">
              <div>
                <strong>
                  {preview.rows.length}
                </strong>
                <span>Tổng dòng</span>
              </div>
              <div>
                <strong className="text-success">
                  {preview.valid}
                </strong>
                <span>Hợp lệ</span>
              </div>
              <div>
                <strong
                  className={
                    preview.errors.length > 0
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {preview.errors.length}
                </strong>
                <span>Lỗi</span>
              </div>
            </div>
          ) : null}

          {preview?.errors.length ? (
            <div className="import-errors">
              {preview.errors
                .slice(0, 10)
                .map((item) => (
                  <small key={item}>
                    {item}
                  </small>
                ))}
            </div>
          ) : null}

          {preview &&
          preview.errors.length === 0 &&
          preview.rows.length > 0 ? (
            <button
              className="btn btn-primary btn-lg"
              disabled={busy}
              onClick={() =>
                void importProducts()
              }
            >
              <ShieldCheck size={20} />{" "}
              {busy
                ? "Đang nhập..."
                : `Tạo ${preview.valid} SKU`}
            </button>
          ) : null}
        </Card>
      </section>
    </div>
  );
}
